/**
 * GAME DATA
 *
 * This file provides backward-compatible exports for game data.
 * The actual data is loaded from the backend by GameDataContext and stored in gameDataStore.
 * These exports use Proxies to dynamically access the data from the store.
 *
 * IMPORTANT: The data will only be available after GameDataProvider has loaded it.
 * Components should use useGameData() hook for reactive access, or ensure they
 * render after GameDataLoadingGate.
 */

import {
  getICONS,
  getEVOLUTION_CONFIG,
  getEVOLUTION_CHAINS,
  getGAME_CONFIG,
  getPOKEMON,
  getLEGENDARY_POKEMON,
  getMOVES,
  getGYM_LEADER_POKEMON,
  getELITE_FOUR,
  getSUPPORT_CARDS,
  getLEGACY_SUPPORT_NAME_MAP,
  getSUPPORT_LIMIT_BREAK_PROGRESSIONS,
  getSUPPORT_GACHA_RARITY,
  getRANDOM_EVENTS,
  getHANGOUT_EVENTS,
  getGACHA_RARITY,
  getGACHA_POOLS_BY_GENERATION,
  getGENERATIONS,
  normalizeSupportName as storeNormalizeSupportName,
  getSupportAtLimitBreak as storeGetSupportAtLimitBreak,
  calculateBaseStats as storeCalculateBaseStats
} from './gameDataStore';

// Create a proxy that dynamically accesses the store
const createDataProxy = (getter) => {
  return new Proxy({}, {
    get: (target, prop) => {
      const data = getter();
      if (prop === Symbol.iterator) {
        return function* () {
          for (const key of Object.keys(data)) {
            yield [key, data[key]];
          }
        };
      }
      if (prop === Symbol.toStringTag) {
        return 'Object';
      }
      return data[prop];
    },
    set: (target, prop, value) => {
      console.warn('Attempted to modify read-only game data');
      return false;
    },
    has: (target, prop) => {
      return prop in getter();
    },
    ownKeys: () => {
      return Object.keys(getter());
    },
    getOwnPropertyDescriptor: (target, prop) => {
      const data = getter();
      if (prop in data) {
        return {
          value: data[prop],
          writable: false,
          enumerable: true,
          configurable: true
        };
      }
      return undefined;
    }
  });
};

// Create a proxy for arrays
const createArrayProxy = (getter) => {
  return new Proxy([], {
    get: (target, prop) => {
      const data = getter();
      if (prop === 'length') {
        return data.length;
      }
      if (prop === Symbol.iterator) {
        return data[Symbol.iterator].bind(data);
      }
      if (typeof prop === 'string' && !isNaN(prop)) {
        return data[parseInt(prop, 10)];
      }
      if (typeof data[prop] === 'function') {
        return data[prop].bind(data);
      }
      return data[prop];
    },
    has: (target, prop) => {
      return prop in getter();
    },
    ownKeys: () => {
      const data = getter();
      return Object.keys(data);
    },
    getOwnPropertyDescriptor: (target, prop) => {
      const data = getter();
      if (prop in data || prop === 'length') {
        return {
          value: data[prop],
          writable: false,
          enumerable: typeof prop === 'string' && !isNaN(prop),
          configurable: true
        };
      }
      return undefined;
    }
  });
};

// Config exports
export const ICONS = createDataProxy(getICONS);
export const EVOLUTION_CONFIG = createDataProxy(getEVOLUTION_CONFIG);
export const EVOLUTION_CHAINS = createDataProxy(getEVOLUTION_CHAINS);
export const GAME_CONFIG = createDataProxy(getGAME_CONFIG);

// Pokemon exports
export const POKEMON = createDataProxy(getPOKEMON);
export const LEGENDARY_POKEMON = createDataProxy(getLEGENDARY_POKEMON);

// Moves exports
export const MOVES = createDataProxy(getMOVES);

// Gym/Elite Four exports
export const GYM_LEADER_POKEMON = createDataProxy(getGYM_LEADER_POKEMON);
export const ELITE_FOUR = createDataProxy(getELITE_FOUR);

// Support cards exports
export const SUPPORT_CARDS = createDataProxy(getSUPPORT_CARDS);
export const LEGACY_SUPPORT_NAME_MAP = createDataProxy(getLEGACY_SUPPORT_NAME_MAP);
export const SUPPORT_LIMIT_BREAK_PROGRESSIONS = createDataProxy(getSUPPORT_LIMIT_BREAK_PROGRESSIONS);
export const SUPPORT_GACHA_RARITY = createDataProxy(getSUPPORT_GACHA_RARITY);

// Events exports
export const RANDOM_EVENTS = createArrayProxy(getRANDOM_EVENTS);
export const HANGOUT_EVENTS = createArrayProxy(getHANGOUT_EVENTS);

// Gacha exports
export const GACHA_RARITY = createDataProxy(getGACHA_RARITY);
export const GACHA_POOLS_BY_GENERATION = createDataProxy(getGACHA_POOLS_BY_GENERATION);
export const GENERATIONS = createDataProxy(getGENERATIONS);

// Helper function exports
export const normalizeSupportName = storeNormalizeSupportName;
export const getSupportAtLimitBreak = storeGetSupportAtLimitBreak;
export const calculateBaseStats = storeCalculateBaseStats;
